/**
 * Product variant controller
 * ------------------------------------------------------------------
 * Single owner of every custom product-option / variant-selection
 * behaviour: option availability (hide / grey-out), Add-to-Cart
 * gating, the sticky form, per-variant inventory display, the collar
 * media/description swap, option-label numbering, the "Select your
 * fit" reminder.
 *
 * The vendor bundle (theme.js) still resolves the variant for
 * price / image / SKU / cart. This controller observes the same
 * option <select> elements and renders everything else from one
 * shared state object, in one deterministic pipeline.
 */
(function () {
  if (window.__variantControllerLoaded) return;
  window.__variantControllerLoaded = true;

  /* ============================================================
   * sticky-product-form custom element (scroll reveal / hide)
   * ========================================================== */
  if (!customElements.get('sticky-product-form')) {
    customElements.define(
      'sticky-product-form',
      class StickyProductForm extends HTMLElement {
        connectedCallback() {
          // Scope to the owning product section so quickview + PDP coexisting
          // on the same page don't cross-wire each other's ATC.
          this.section = this.closest('[data-section-type="product"]') || document;
          this.productForm = this.section.querySelector('.product__meta');
          this.productStickyForm = this.section.querySelector('.product--sticky-form');
          this.atcButton = this.section.querySelector('#product-form-button');
          this.onScrollHandler = this.onScroll.bind(this);
          window.addEventListener('scroll', this.onScrollHandler, false);
          this.createObserver();
        }

        disconnectedCallback() {
          window.removeEventListener('scroll', this.onScrollHandler);
          if (this.observer) this.observer.disconnect();
        }

        createObserver() {
          if (!this.productForm) return;
          this.observer = new IntersectionObserver(entries => {
            this.productFormBounds = entries[0].isIntersecting;
          });
          this.observer.observe(this.productForm);
        }

        onScroll() {
          const button = this.atcButton;
          const stickyProductForm = this;
          const chatButton = document.getElementById('chat-button');
          const backToTop = document.querySelector('.back-to-top.visible');
          if (!button) return;

          const visible =
            stickyProductForm &&
            window.getComputedStyle(stickyProductForm).getPropertyValue('visibility') === 'visible';

          if (button.getBoundingClientRect().top < 0) {
            if (visible && chatButton && backToTop) {
              chatButton.style.bottom = '170px';
              backToTop.style.bottom = '230px';
            }
            requestAnimationFrame(this.reveal.bind(this));
          } else {
            if (visible && chatButton && backToTop) {
              chatButton.style.bottom = '20px';
              backToTop.style.bottom = '80px';
            }
            requestAnimationFrame(this.hide.bind(this));
          }
        }

        hide() {
          if (!this.productStickyForm) return;
          if (this.productStickyForm.classList.contains('product--sticky-form__active')) {
            this.productStickyForm.classList.remove('product--sticky-form__active');
            this.productStickyForm.classList.add('product--sticky-form__inactive');
          }
        }

        reveal() {
          if (!this.productStickyForm) return;
          if (this.productStickyForm.classList.contains('product--sticky-form__inactive')) {
            this.productStickyForm.classList.add('product--sticky-form__active');
            this.productStickyForm.classList.remove('product--sticky-form__inactive');
          }
        }
      }
    );
  }

  /* ============================================================
   * VariantState — the single shared state object per scope
   * ========================================================== */
  class VariantState {
    constructor(container) {
      this.container = container;
      this.variants = this._readVariantJson();
      this.optionCount = this.variants.length ? this.variants[0].options.length : 0;

      // Button-style option groups (swatches / chips) — main form only.
      this.buttonGroups = [...container.querySelectorAll('[data-option-buttons]')].map(el => ({
        el,
        index: el.dataset.productOption, // "option1" | "option2" | "option3"
        title: (el.dataset.optionTitle || '').trim(),
        buttons: [...el.querySelectorAll('[data-button]')],
      }));

      // Native dropdown options (no buttons) — variant resolution only.
      this.dropdownSelects = [...container.querySelectorAll('select[name^="options["]')].filter(
        s => !s.closest('[data-option-buttons]')
      );

      this.selected = {};
      this.currentVariant = null;
      this.inStock = {};   // inStock[index] = Set of values with an available match
    }

    _readVariantJson() {
      const el = this.container.querySelector('[data-variant-json]');
      if (!el) return [];
      try {
        return JSON.parse(el.textContent) || [];
      } catch (e) {
        console.error('[variant-controller] bad variant JSON', e);
        return [];
      }
    }

    _indexes() {
      const out = [];
      for (let n = 1; n <= this.optionCount; n++) out.push('option' + n);
      return out;
    }

    /** Re-read the current selection from the DOM. */
    refresh() {
      this.selected = {};
      this._indexes().forEach(index => {
        const group = this.buttonGroups.find(g => g.index === index);
        if (group) {
          const btn = group.buttons.find(b => b.classList.contains('selected'));
          this.selected[index] = btn ? btn.dataset.optionValue : null;
        } else {
          const select = this.container.querySelector('select[data-index="' + index + '"]');
          this.selected[index] = select ? select.value : null;
        }
      });

      const allChosen = this._indexes().every(i => this.selected[i] != null);
      this.currentVariant = allChosen
        ? this.variants.find(v => this._indexes().every(i => v[i] === this.selected[i])) || null
        : null;

      this._recomputeAvailability();
    }

    /**
     * For each option value decide whether it is in stock (drives grey-out), given the
     * *other* currently-selected options.
     */
    _recomputeAvailability() {
      this.inStock = {};
      this._indexes().forEach(index => {
        const others = this._indexes().filter(i => i !== index && this.selected[i] != null);
        const inStock = new Set();
        this.variants.forEach(v => {
          if (!others.every(i => v[i] === this.selected[i])) return;
          if (v.available) inStock.add(v[index]);
        });
        this.inStock[index] = inStock;
      });
    }

    /** A button group counts as "chosen" once it has a selected button. */
    isGroupChosen(group) {
      return group.buttons.some(b => b.classList.contains('selected'));
    }
  }

  /* ============================================================
   * Sub-renderers — all read VariantState, none read each other
   * ========================================================== */

  // Discontinued size/fit combinations that must never appear.
  const DISCONTINUED = {
    'Tall Fit':  new Set(['S', 'M']),
    'Slim Fit':  new Set(['2XL', '3XL']),
  };

  const Availability = {
    /**
     * Grey out (.disabled) every value that isn't purchasable for the current selections.
     */
    render(state) {
      const selectedFit = state.selected.option2 || null;

      state.buttonGroups.forEach(group => {
        const inStock = state.inStock[group.index] || new Set();
        const isSizeGroup = group.title.toLowerCase() === 'size';
        const isCollarGroup = group.title.toLowerCase() === 'collar';

        group.buttons.forEach(btn => {
          // Collar values with no configured image are hidden by Liquid;
          // never resurrect them.
          if (btn.hasAttribute('data-option-unavailable')) return;
          const value = btn.dataset.optionValue;
          btn.classList.toggle('disabled', !isCollarGroup && !inStock.has(value)); // Exempt collar from gray-out

          // Hard-coded one-off: hide discontinued sizes for specific fits. Use toggle
          // so a size reappears when the fit changes away from one that hides it
          const hideDiscontinued =
            isSizeGroup && selectedFit && DISCONTINUED[selectedFit]
              ? DISCONTINUED[selectedFit].has(value)
              : false;
          btn.classList.toggle('d-none', hideDiscontinued);
        });
      });
    },
  };

  const AutoSelect = {
    /**
     * Returns true if it clicked a button (which re-enters the pipeline).
     * Selects the only visible value in a group, and re-selects the
     * nearest visible value when the current selection became hidden.
     */
    run(state) {
      for (const group of state.buttonGroups) {
        const visible = group.buttons.filter(
          b => !b.classList.contains('d-none') && !b.hasAttribute('data-option-unavailable')
        );
        if (!visible.length) continue;

        const selectedBtn = group.buttons.find(b => b.classList.contains('selected'));

        if (visible.length === 1) {
          if (!visible[0].classList.contains('selected')) {
            visible[0].click();
            return true;
          }
          continue;
        }

        if (selectedBtn && selectedBtn.classList.contains('d-none')) {
          const fromIndex = group.buttons.indexOf(selectedBtn);
          const nearest = visible
            .slice()
            .sort(
              (a, b) =>
                Math.abs(group.buttons.indexOf(a) - fromIndex) -
                Math.abs(group.buttons.indexOf(b) - fromIndex)
            )
            .sort((a, b) => a.classList.contains('disabled') - b.classList.contains('disabled'))[0];
          if (nearest) {
            nearest.click();
            return true;
          }
        }
      }
      return false;
    },
  };

  const ATCGate = {
    /** Disable + relabel the main Add-to-Cart button. */
    render(state) {
      const target = [...state.container.querySelectorAll('[data-add-to-cart]')].find(
        b => !b.closest('sticky-product-form')
      );
      if (!target) return;
      const label = target.querySelector('[data-add-to-cart-text]');
      if (!label) return;

      const unchosen = state.buttonGroups.find(g => !state.isGroupChosen(g));
      if (unchosen) {
        target.disabled = true;
        label.textContent = 'Select ' + unchosen.title;
        return;
      }
      // Disable unless a real, available variant is resolved.
      const variant = state.currentVariant;
      const available = !!(variant && variant.available);
      target.disabled = !available;
      label.textContent = available
        ? target.dataset.langAvailable || 'Add to cart'
        : target.dataset.langSoldOut || 'Sold out';
    },
  };

  const InventoryDisplay = {
    init(state) {
      const counter = state.container.querySelector('[data-inventory-counter]');
      const json = state.container.querySelector('[data-product-inventory-json]');
      if (!counter || !json) return null;
      let inventory = {};
      try {
        inventory = (JSON.parse(json.textContent) || {}).inventory || {};
      } catch (e) {
        console.error('[variant-controller] bad inventory JSON', e);
      }
      return {
        inventory,
        a1: counter.querySelector('.stock'),
        a2: counter.querySelector('.stock-abtestB--text'),
        dot: counter.querySelector('.stock-abtestB--circle'),
        msg: {
          inStock: counter.dataset.invInStock || '',
          inStockSimple: counter.dataset.invInStockSimple || '',
          lowStock: counter.dataset.invLowStock || '',
          soldOut: counter.dataset.invSoldOut || '',
        },
      };
    },

    render(state, inv) {
      if (!inv) return;
      const data = state.currentVariant ? inv.inventory[state.currentVariant.id] : null;
      if (!data) {
        this._paint(inv, inv.msg.inStock, inv.msg.inStockSimple, '#008000', false);
        return;
      }
      const status = data.inventory_status;
      if (status === 'out-of-stock') {
        this._paint(inv, inv.msg.soldOut, inv.msg.soldOut, '#D02F2E', true);
      } else if (status === 'low-stock') {
        const text = inv.msg.lowStock.replace('[count]', data.inventory_quantity);
        this._paint(inv, text, text, '#FFA500', false);
      } else {
        this._paint(inv, inv.msg.inStock, inv.msg.inStockSimple, '#008000', false);
      }
    },

    _paint(inv, a1Text, a2Text, dotColor, outOfStock) {
      if (inv.a1) {
        inv.a1.textContent = a1Text;
        inv.a1.classList.toggle('out-of-stock', outOfStock);
        inv.a1.classList.toggle('stock-in', !outOfStock);
      }
      if (inv.a2) inv.a2.textContent = a2Text;
      if (inv.dot) inv.dot.style.backgroundColor = dotColor;
    },
  };

  const CollarMediaSwap = {
    /** Swap variant thumbnails + description accordion by collar/fit/size. */
    render(state) {
      const thumbWrap = state.container.querySelector('#variant-thumbnails');
      const thumbnails = thumbWrap
        ? [...thumbWrap.querySelectorAll('.product-thumbnails__item')]
        : [];
      const accordions = [...state.container.querySelectorAll('.ctm-accordion--product')].filter(
        a => a.querySelector('.collar_data')
      );
      if (!thumbnails.length && !accordions.length) return;

      const collar = state.selected.option1;
      const fit = state.selected.option2;
      const size = state.selected.option3;
      const activeCollar =
        collar || (thumbnails[0] && thumbnails[0].getAttribute('data-media-alt')) || '';

      thumbnails.forEach(t => {
        t.classList.toggle('d-none', t.getAttribute('data-media-alt') !== activeCollar);
      });

      const tokens = attr =>
        (attr || '').split(',').map(s => s.trim()).filter(Boolean);

      accordions.forEach(accordion => {
        const sets = [...accordion.querySelectorAll('.collar_data')];
        let topMatch = 0;
        let best = null;
        sets.forEach(set => {
          set.style.display = 'none';
          if (set.getAttribute('data-value') !== activeCollar) return;
          const setFits = tokens(set.getAttribute('data-value-fit'));
          const setSizes = tokens(set.getAttribute('data-value-size'));
          let level = 1;
          if (fit && setFits.includes(fit)) level++;
          if (size && setSizes.includes(size)) level++;
          if (level > topMatch) {
            topMatch = level;
            best = set;
          }
        });
        if (best) best.style.display = 'block';
      });
    },
  };

  const FitPopupBar = {
    /** Show "Select your fit" until a fit is chosen but a size was picked. */
    render(state) {
      const popups = [...state.container.querySelectorAll('.fit-popup-container')];
      if (!popups.length) return;
      const fit = state.buttonGroups.find(g => g.title.toLowerCase() === 'fit');
      const size = state.buttonGroups.find(g => g.title.toLowerCase() === 'size');
      const show =
        fit && size && !state.isGroupChosen(fit) && state.isGroupChosen(size);
      popups.forEach(p => {
        p.classList.toggle('show', show);
        p.classList.toggle('d-none', !show);
      });
    },
  };

  const LabelNumbering = {
    /** Number the option labels — "1. Select Fit:", "2. Select Size:" … */
    render(state) {
      const elements = [...state.container.querySelectorAll('.PDPRedesign')];
      const numbered = ['Collar', 'Fit', 'Size'];

      const oneVisibleButton = el => {
        const group = el.closest('[data-product-option]');
        if (!group) return false;
        return (
          group.querySelectorAll('[data-button]:not([data-option-unavailable]):not(.d-none)')
            .length === 1
        );
      };

      const active = elements.filter(el => {
        const text = (el.dataset.optionLabelText || el.textContent || '').trim();
        return (
          numbered.includes(text) &&
          !el.hasAttribute('has-single-option') &&
          !oneVisibleButton(el)
        );
      });

      elements.forEach(el => {
        if (!el.dataset.optionLabelText) el.dataset.optionLabelText = (el.textContent || '').trim();
        const text = el.dataset.optionLabelText;
        let label;
        if (
          !numbered.includes(text) ||
          el.hasAttribute('has-single-option') ||
          oneVisibleButton(el)
        ) {
          label = (text + ':').replace('::', ':');
        } else if (active.length <= 1) {
          label = 'Select ' + text + ':';
        } else {
          label = active.indexOf(el) + 1 + '. Select ' + text + ': ';
        }
        el.textContent = label;
        requestAnimationFrame(() => {
          el.style.opacity = '1';
        });
      });
    },
  };

  /* ============================================================
   * Sticky form view — renders from VariantState
   * ========================================================== */
  class StickyView {
    constructor(controller) {
      this.controller = controller;
      this.container = controller.container;
      this.sticky = this.container.querySelector('sticky-product-form');
      if (!this.sticky) return;

      this.groups = [...this.sticky.querySelectorAll('[data-option-title]')].map(el => {
        const title = (el.dataset.optionTitle || '').trim();
        return {
          el,
          title,
          buttons: [...el.querySelectorAll('[data-button]')],
          label: this.container.querySelector('[data-option-selected-label="' + title + '"]'),
          stepLabel: this.sticky.querySelector('[data-option-step-label="' + title + '"]'),
        };
      });

      this._bindClicks();
      this._bindPanel();
    }

    /**
     * Find the main-form source for a sticky group:
     *   - button group (swatches / chips)  → click delegation
     *   - hidden <select> (dropdown option) → set value + dispatch change
     */
    _mainTarget(title) {
      const state = this.controller.state;
      const group = state.buttonGroups.find(
        g => g.title.toLowerCase() === title.toLowerCase()
      );
      if (group) return { kind: 'buttons', group, index: group.index };
      const select = state.dropdownSelects.find(s => {
        const m = (s.name || '').match(/^options\[(.+)\]$/);
        return m && m[1].trim().toLowerCase() === title.toLowerCase();
      });
      if (select) {
        const index = select.dataset.index;
        if (!index) {
          console.warn('[variant-controller] dropdown select missing data-index', select);
          return null;
        }
        return { kind: 'select', select, index };
      }
      return null;
    }

    _bindClicks() {
      this.groups.forEach(group => {
        group.buttons.forEach(btn => {
          btn.addEventListener('click', () => {
            const target = this._mainTarget(group.title);
            if (!target) return;
            if (target.kind === 'buttons') {
              const match = target.group.buttons.find(
                b => b.dataset.optionValue === btn.dataset.optionValue
              );
              if (match) match.click();
            } else {
              target.select.value = btn.dataset.optionValue;
              target.select.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        });
      });
    }

    _bindPanel() {
      const stickyButton = this.sticky.querySelector('#stickty-button');
      const options = this.sticky.querySelector('.product--sticky-form__options');
      const content = this.sticky.querySelector('.product--sticky-form__content');
      if (stickyButton && options) {
        stickyButton.addEventListener('click', event => {
          event.preventDefault();
          const closed = !options.style.display || options.style.display === 'none';
          if (closed) {
            options.style.display = 'flex';
            if (content) content.style.display = 'none';
            this.render();
            return;
          }
          const mainATC = this.container.querySelector('#product-form-button');
          if (mainATC) mainATC.click();
        });
      }

      const findMySize = document.getElementById('findMySize');
      const findTarget = document.getElementById('titleFindSize');
      if (findMySize) {
        if (findTarget) {
          findMySize.addEventListener('click', () =>
            findTarget.scrollIntoView({ behavior: 'smooth' })
          );
        } else {
          findMySize.style.display = 'none';
        }
      }
    }

    render() {
      if (!this.sticky) return;
      const state = this.controller.state;

      this.groups.forEach(group => {
        const target = this._mainTarget(group.title);
        if (!target) return;
        const value = state.selected[target.index];

        group.buttons.forEach(btn => {
          const v = btn.dataset.optionValue;
          btn.classList.toggle('selected', v === value);
          if (target.kind === 'buttons') {
            const mainBtn = target.group.buttons.find(b => b.dataset.optionValue === v);
            if (mainBtn) {
              btn.classList.toggle('d-none', mainBtn.classList.contains('d-none'));
              btn.classList.toggle('disabled', mainBtn.classList.contains('disabled'));
            }
          } else {
            // Dropdown options have no per-value hide/disable state on the main form.
            btn.classList.remove('d-none', 'disabled');
          }
        });

        if (group.label) group.label.textContent = value || '';

        // Mirror the numbered step label from the main PDP's .PDPRedesign so
        // "1. Select Fit:" etc. appears in the sticky bar too.
        if (group.stepLabel) {
          const sourceEl = target.kind === 'buttons' ? target.group.el : target.select;
          const pdpRedesign = sourceEl.closest('.product__option')?.querySelector('.PDPRedesign');
          if (pdpRedesign) group.stepLabel.textContent = pdpRedesign.textContent;
        }
      });

      // Mirror price.
      const price = this.container.querySelector('[data-price]');
      const stickyPrice = this.sticky.querySelector('#price-sticky');
      if (price && stickyPrice) {
        stickyPrice.textContent = (price.textContent || '').trim();
      }

      // Mirror Add-to-Cart state only while the panel is open — when
      // closed the button must stay enabled so it can open the panel.
      const options = this.sticky.querySelector('.product--sticky-form__options');
      if (options && options.style.display === 'flex') {
        const mainATC = this.container.querySelector('#product-form-button');
        const stickyATC = this.sticky.querySelector('[data-add-to-cart]');
        if (mainATC && stickyATC) {
          stickyATC.disabled = mainATC.disabled;
          const mainLabel = mainATC.querySelector('[data-add-to-cart-text]');
          const stickyLabel = stickyATC.querySelector('[data-add-to-cart-text]');
          if (mainLabel && stickyLabel) stickyLabel.textContent = mainLabel.textContent;
        }
      }
    }
  }

  /* ============================================================
   * ProductVariantController — owns one product-form scope
   * ========================================================== */
  class ProductVariantController {
    constructor(container) {
      this.container = container;
      this.state = new VariantState(container);
      this.inventory = InventoryDisplay.init(this.state);
      this.sticky = new StickyView(this);

      this._running = false;
      this._dirty = false;
      this._labelsDone = false;

      // Re-run after the vendor resolves the variant. theme.js dispatches
      // `product:variant-change` on document at the END of its own option-change handler
      // (after price / SKU / buy-button are updated, theme.js:6522), so reacting here means
      // our pipeline — and ATCGate — runs after the vendor instead of racing it.
      // The listener is on document because the vendor dispatches there; filter by
      // variant id so a sibling controller (PDP + quickview both mounted) doesn't
      // re-run our pipeline on every option change in the other form.
      this._variantIds = new Set(this.state.variants.map(v => v.id));
      this._onVariantChange = event => {
        const id = event.detail && event.detail.variant && event.detail.variant.id;
        if (id != null && !this._variantIds.has(id)) return;
        this.runPipeline();
      };
      document.addEventListener('product:variant-change', this._onVariantChange);

      this.runPipeline();
    }

    dispose() {
      document.removeEventListener('product:variant-change', this._onVariantChange);
    }

    /** Re-entrancy-safe: AutoSelect clicks re-enter synchronously. */
    runPipeline() {
      if (this._running) {
        this._dirty = true;
        return;
      }
      this._running = true;
      let guard = 0;
      do {
        this._dirty = false;
        this._pipeline();
        if (++guard > 30) {
          console.warn('[variant-controller] pipeline did not settle');
          break;
        }
      } while (this._dirty);
      this._running = false;
    }

    _pipeline() {
      this.state.refresh();
      Availability.render(this.state);
      // AutoSelect clicks a button to change the selection; loop so the new selection is
      // rendered. Self-driven via _dirty so it no longer depends on an option-change event
      // firing (the vendor's listener may not be attached yet on first paint).
      if (AutoSelect.run(this.state)) {
        this._dirty = true;
        return;
      }
      // LabelNumbering must run before sticky.render so the sticky bar can
      // mirror the numbered ".PDPRedesign" labels on the very first pass.
      if (!this._labelsDone) {
        LabelNumbering.render(this.state);
        this._labelsDone = true;
      }
      ATCGate.render(this.state);
      InventoryDisplay.render(this.state, this.inventory);
      CollarMediaSwap.render(this.state);
      FitPopupBar.render(this.state);
      this.sticky.render();
    }
  }

  /* ============================================================
   * Bootstrap — one controller per product-form scope
   * ========================================================== */
  const registry = new Map();

  function initScope(root) {
    const sections = root.matches && root.matches('[data-section-type="product"]')
      ? [root]
      : [...root.querySelectorAll('[data-section-type="product"]')];
    sections.forEach(section => {
      if (registry.has(section)) return;
      try {
        registry.set(section, new ProductVariantController(section));
      } catch (e) {
        console.error('[variant-controller] init failed', e);
      }
    });
  }

  function disposeScope(root) {
    registry.forEach((controller, section) => {
      if (root === section || (root.contains && root.contains(section))) {
        controller.dispose();
        registry.delete(section);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initScope(document));
  } else {
    initScope(document);
  }

  document.addEventListener('quickview:loaded', event => {
    const node = event.detail && event.detail.node;
    if (node) initScope(node);
  });

  document.addEventListener('quickview:closed', event => {
    const node = event.detail && event.detail.node;
    if (node) disposeScope(node);
  });
})();
